import { useState, useRef, useCallback } from 'react';
import useTipClamp from './useTipClamp';

/**
 * InfoBox — wraps a result tile (or any small box) with a multi-line
 * hover/tap explainer popup. Same off-screen-clamping pattern as
 * HoverBar, but supports wrapped text + a max-width so explainer
 * paragraphs (e.g. "Yield on Cost = annual dividends ÷ everything
 * you've put into stocks…") render cleanly without overflowing the
 * modal or the viewport edge.
 *
 * Behavior:
 *   • Desktop: show on mouseenter, hide on mouseleave.
 *   • Mobile:  show on touchstart, auto-hide after 2.2s.
 *   • Tooltip auto-shifts horizontally if it would overflow the
 *     nearest clipping ancestor (uses `useTipClamp`).
 */
export default function InfoBox({
  children,
  title,
  body,
  className = '',
  testId,
}) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const tipRef = useRef(null);
  // shiftX clamps the tooltip horizontally so it never overflows the
  // nearest scrollable / clipping ancestor (the calculator modal).
  const shiftX = useTipClamp(tipRef, open, [title, body]);

  const show = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);
  const touchShow = useCallback(
    (e) => {
      e.stopPropagation();
      show();
      timerRef.current = setTimeout(() => setOpen(false), 2200);
    },
    [show],
  );

  return (
    <div
      className={`relative ${className}`}
      data-testid={testId}
      onMouseEnter={show}
      onMouseLeave={hide}
      onTouchStart={touchShow}
    >
      {children}
      {open && (title || body) && (
        <div
          ref={tipRef}
          className="absolute left-1/2 -top-2 z-30 w-64 max-w-[calc(100vw-2rem)] bg-zinc-900 text-white text-[11px] leading-snug rounded-lg shadow-xl px-3 py-2 select-none"
          style={{ transform: `translate(calc(-50% + ${shiftX}px), -100%)` }}
          data-testid={testId ? `${testId}-tooltip` : undefined}
        >
          {title && <div className="font-bold mb-1 text-[12px]">{title}</div>}
          {body && <div className="text-zinc-200 whitespace-pre-line">{body}</div>}
          {/* Arrow — kept aligned with the original box midline even when
              the body has shifted left/right to avoid overflow. */}
          <span
            className="absolute top-full -mt-px border-[6px] border-transparent"
            style={{
              left: `calc(50% - ${shiftX}px)`,
              transform: 'translateX(-50%)',
              borderTopColor: '#18181b',
            }}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}
